/**
 * The Keenon mock's control plane, called DIRECTLY from the browser.
 *
 * Deliberately not routed through the adapter over MQTT. The "browser never reaches the
 * vendor" rule exists because vendor credentials and vendor rate limits must not be
 * exposed to a browser; the mock is our own harness, holds neither, and routing its
 * start/stop through the adapter would have been ceremony rather than safety. It also
 * keeps the adapter's debug surface strictly read-only (change_requests/008).
 *
 * THE KEY IS NOT A SECRET. It is baked into this bundle, which is static and public —
 * anyone who opens devtools can read it. It gates casual access, nothing more. What
 * actually keeps production safe is structural: this mock feeds a SEPARATE adapter
 * deployment that publishes to site `norbot-test`, so the worst an attacker can do here
 * is pause a fabricated robot in a test namespace.
 *
 * The whole panel is absent unless the build was given a URL and a key, so a plain
 * production build ships no debug surface at all.
 */
import { cfg } from "./config";

export type Lifecycle = "active" | "dormant" | "absent";
export type Verb = "wake" | "sleep" | "kill";

export interface MockRobot {
  lifecycle: Lifecycle;
  /** The robot's OWN scenario time. Frozen while it is not active. */
  t: number;
  taskState?: string;
}

export interface MockState {
  scenarioId: string;
  periodS: number;
  t: number;
  robots: Record<string, MockRobot>;
}

/** Configured at build time? If not, the panel does not exist. */
export function mockConfigured(): boolean {
  return !!cfg.mockUrl && !!cfg.mockKey;
}

async function call(path: string, method: "GET" | "POST"): Promise<any> {
  const res = await fetch(`${cfg.mockUrl}${path}`, {
    method,
    headers: { "X-Mock-Key": cfg.mockKey, "Content-Type": "application/json" },
    // The mock is a different origin, so this is a CORS request with a custom header and
    // therefore always preflighted. The mock answers OPTIONS with `Allow-Headers: *`.
    mode: "cors",
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    // 403 and 401 need different fixes and must not be collapsed: 403 means the MOCK has
    // no key configured (its control plane is disabled, a deploy problem), 401 means the
    // key this bundle carries is wrong (a build problem).
    const hint = res.status === 403
      ? "the mock has no KEENON_MOCK_KEY set, so its control plane is disabled"
      : res.status === 401
        ? "this build's VITE_MOCK_KEY does not match the mock's"
        : body?.msg ?? res.statusText;
    throw new Error(`${res.status}: ${hint}`);
  }
  return body;
}

/**
 * Current scenario and per-robot lifecycle.
 *
 * A failure here is EXPECTED and not an error to shout about: the mock is normally
 * stopped, and fly starts it on request — so the first call after an idle period can
 * take ten seconds or fail once while the machine boots.
 */
export async function fetchState(): Promise<MockState> {
  const d = await call("/_mock/state", "GET");
  const robots: Record<string, MockRobot> = {};
  for (const [sn, r] of Object.entries<any>(d.robots ?? {})) {
    robots[sn] = {
      lifecycle: r.lifecycle,
      t: r.t,
      taskState: r.state?.task?.state,
    };
  }
  return {
    scenarioId: d.scenario?.scenarioId ?? "?",
    periodS: d.scenario?.periodS ?? 0,
    t: d.t ?? 0,
    robots,
  };
}

/** Move a robot (or `all`) between lifecycle states. */
export async function setLifecycle(sn: string, verb: Verb): Promise<void> {
  const d = await call(`/_mock/robot/${encodeURIComponent(sn)}/${verb}`, "POST");
  // The mock answers business failures as HTTP 200 with a non-OK `code` — the vendor's
  // own convention, reproduced. Checking only the status would report success for a
  // refused command, which is the exact trap the adapter has a razor about.
  if (d && d.ok !== true) throw new Error(d.msg ?? `refused (code ${d.code})`);
}

/**
 * Is the mock awake? Unauthenticated, and it also WAKES a stopped machine, which is why
 * the panel calls it before anything else.
 */
export async function isUp(timeoutMs = 4000): Promise<boolean> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetch(`${cfg.mockUrl}/health`, { signal: ctl.signal, mode: "cors" });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
